import {
  KeywordInsightsCampaignType,
  KeywordInsightsCampaignCondition,
  KeywordsInsightsDirection,
} from "@whatslly/types/keyword-insights/KeywordInsightsCampaign";
import { UserType } from "@whatslly/types/User";
import escapeRegExp from "lodash/escapeRegExp";

export type TestKeywordCampaignParams = {
  content: string;
  user?: Partial<Pick<UserType, "userId" | "groupIds">> | undefined;
  direction?: KeywordsInsightsDirection;
};

export const testCampaignConditions = (campaign: KeywordInsightsCampaignType, params: TestKeywordCampaignParams | string): boolean => {
  const resolvedParams: TestKeywordCampaignParams = typeof params === "string" ? { content: params } : params;
  const hasInbound = campaign.conditions.some((c) => c.type === "direction" && c.direction === KeywordsInsightsDirection.INBOUND);
  const hasOutbound = campaign.conditions.some((c) => c.type === "direction" && c.direction === KeywordsInsightsDirection.OUTBOUND);
  if (!hasInbound && !hasOutbound) {
    // support legacy campaigns
    campaign.conditions.push({ type: "direction", direction: KeywordsInsightsDirection.OUTBOUND });
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  campaign.conditions.forEach((c: any) => {
    // support legacy conditions
    if (!c.type) c.type = "expression";
  });

  for (const condition of campaign.conditions) {
    if (condition.type === "direction" && condition.direction === KeywordsInsightsDirection.INBOUND && hasOutbound) {
      continue;
    }
    if (condition.type === "direction" && condition.direction === KeywordsInsightsDirection.OUTBOUND && hasInbound) {
      continue;
    }
    if (!testConditionSync(condition, resolvedParams)) {
      return false;
    }
  }
  return true;
};

const testConditionSync = (condition: KeywordInsightsCampaignCondition, params: TestKeywordCampaignParams): boolean => {
  switch (condition.type) {
    case "expression": {
      const regex = new RegExp(escapeRegExp(condition.expression), `g${condition.caseSensitive ? "" : "i"}`);
      const matches = params.content.match(regex);
      return matches !== null && matches.length > 0;
    }
    case "user":
      return condition.userId.includes(params.user?.userId ?? "");
    case "group":
      return condition.groupId.some((groupId) => params.user?.groupIds?.includes(groupId));
    case "direction":
      return condition.direction === params.direction;
  }
};

export const getCampaignMatchedKeywords = (campaign: KeywordInsightsCampaignType, content: string) => {
  const expressionConditions = campaign.conditions.filter(
    (c): c is Extract<KeywordInsightsCampaignCondition, { type: "expression" }> => c.type === "expression" || !c.type
  );
  const expressionMap = new Map(expressionConditions.map((e) => [new RegExp(e.expression, `g${e.caseSensitive ? "" : "i"}`), e.max]));
  const matches = Array.from(expressionMap.keys()).reduce<string[]>((acc, exp) => {
    const match = content.match(exp);
    if (match) {
      acc.push(...match);
    }
    return acc;
  }, []);
  return [...new Set(matches)];
};
